import { baseApi } from "../base-api"

// Define API response types
export interface Invoice {
  id: string
  secondaryId?: number
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  clientName: string
  description: string
  taxRate: number
  subtotal: number
  totalAmount: number
  status: string
  items: InvoiceItem[]
  containers: InvoiceContainer[]
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  NOT_SEND = "NOT_SEND",
  SENT = "SENT",
  PAID = "PAID",
  CANCELLED = "CANCELLED"
}

export const invoiceStatusOptions = [
  { label: "Draft", value: InvoiceStatus.DRAFT },
  { label: "Not Send", value: InvoiceStatus.NOT_SEND },
  { label: "Sent", value: InvoiceStatus.SENT },
  { label: "Paid", value: InvoiceStatus.PAID },
  { label: "Cancelled", value: InvoiceStatus.CANCELLED }
]
interface InvoiceItem {
  itemId?: string
  name?: string
  description?: string
  unitPrice?: number
  unit?: number
  totalPrice?: number
}

interface InvoiceContainer {
  containerId?: string
  containerNos?: string
  truck?: string
  gd?: string
  cnee?: string
  term?: string
}

export interface CreateInvoiceParams {
  invoiceNumber?: string
  invoiceDate?: string
  dueDate?: string
  clientName?: string
  description?: string
  taxRate: number
  status: string
  items: InvoiceItem[]
  // containers: InvoiceContainer[]
  containerIds?: string[]
  portId?: string
  vendorId?: string
  containerTypeId?: string
}

interface CreateInvoiceResponse {
  data: Invoice
  meta: null
  msg: string
  success: boolean
}

interface GetInvoiceParams {
  id: string
}

interface GetInvoiceResponse {
  data: Invoice
  meta: null
  msg: string
  success: boolean
}

interface GetInvoicesParams {
  page?: number
  limit?: number
  status?: string
  invoiceNumber?: string
  clientName?: string
}

interface GetInvoicesResponse {
  data: Invoice[]
  meta: null
  msg: string
  success: boolean
}

export interface UpdateInvoiceParams extends CreateInvoiceParams {
  id: string
}

interface UpdateInvoiceResponse {
  data: Invoice | null
  meta: null
  msg: string
  success: boolean
}

interface DeleteInvoiceParams {
  id: string
}

interface DeleteInvoiceResponse {
  success: boolean
  message: string
}

// API slice for invoice module
export const invoiceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all invoices with pagination and search
    getInvoices: builder.query<GetInvoicesResponse, GetInvoicesParams>({
      query: params => ({
        url: `invoices`,
        method: "GET",
        params
      }),
      providesTags: ["Invoice"]
    }),

    // Get single invoice by ID
    getInvoice: builder.query<GetInvoiceResponse, GetInvoiceParams>({
      query: ({ id }) => ({
        url: `invoices/${id}`,
        method: "GET"
      }),
      providesTags: ["Invoice"]
    }),

    // Create new invoice
    createInvoice: builder.mutation<CreateInvoiceResponse, CreateInvoiceParams>({
      query: invoice => ({
        url: "invoices",
        method: "POST",
        body: invoice
      }),
      invalidatesTags: ["Invoice"]
    }),

    // Update existing invoice
    updateInvoice: builder.mutation<UpdateInvoiceResponse, UpdateInvoiceParams>({
      query: ({ id, ...invoice }) => ({
        url: `invoices/${id}`,
        method: "PATCH",
        body: invoice
      }),
      invalidatesTags: ["Invoice"]
    }),

    // Delete invoice
    deleteInvoice: builder.mutation<DeleteInvoiceResponse, DeleteInvoiceParams>({
      query: id => ({
        url: `invoices/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Invoice"]
    })
  })
})

// Export hooks for use in components
export const {
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation
} = invoiceApi
